/**
 * Node Type Definitions
 * Complete definitions for all 11 content types from REQUIREMENTS.md
 */

import { NodeTypeId, EdgeCardinality, Language } from '../types/graph.types';

export type DataType = 'text' | 'int' | 'num' | 'bool';
export type EditRenderType = 'text' | 'longtext' | 'markdown' | 'bool';

export interface FieldDefinition {
  name: string;
  dataType: DataType;
  editRenderType: EditRenderType;
  isLanguageField?: boolean;
  editingDisabled?: boolean;
}

export interface EdgeDefinition {
  name: string;
  cardinality: EdgeCardinality;
  targetNodeTypeId: NodeTypeId;
}

export interface NodeTypeDefinition {
  nodeTypeId: NodeTypeId;
  name: string;
  pluralName: string;
  description: string;
  fields: FieldDefinition[];
  edges: EdgeDefinition[];
}

// All supported languages for field generation
const SUPPORTED_LANGUAGES: Language[] = [
  Language.TE,
  Language.EN,
  Language.KN,
  Language.TA,
  Language.HI,
  Language.ML,
  Language.OR,
  Language.BN,
  Language.PA,
  Language.GU,
];

function generateLanguageNameFields(): FieldDefinition[] {
  const fields: FieldDefinition[] = [];

  for (const lang of SUPPORTED_LANGUAGES) {
    fields.push({
      name: `${lang}Name`,
      dataType: 'text',
      editRenderType: 'text',
      isLanguageField: true,
    });
    fields.push({
      name: `${lang}NameOverwrite`,
      dataType: 'bool',
      editRenderType: 'bool',
      isLanguageField: true,
    });
  }

  fields.push({
    name: 'enNormalizedName',
    dataType: 'text',
    editRenderType: 'text',
    isLanguageField: false,
    editingDisabled: true,
  });

  return fields;
}

function generateLanguageTextFields(): FieldDefinition[] {
  const fields: FieldDefinition[] = [];

  for (const lang of SUPPORTED_LANGUAGES) {
    fields.push({
      name: `${lang}Text`,
      dataType: 'text',
      editRenderType: 'longtext',
      isLanguageField: true,
    });
  }

  return fields;
}

function generateLanguageMeaningFields(): FieldDefinition[] {
  const fields: FieldDefinition[] = [];

  for (const lang of SUPPORTED_LANGUAGES) {
    fields.push({
      name: `${lang}Text`,
      dataType: 'text',
      editRenderType: 'markdown',
      isLanguageField: true,
    });
  }

  return fields;
}

// NodeTypeId 1: Bhajan
export const BhajanNodeType: NodeTypeDefinition = {
  nodeTypeId: NodeTypeId.BHAJAN,
  name: 'bhajan',
  pluralName: 'bhajans',
  description: 'Represents a bhajan (devotional song) with metadata',
  fields: [
    ...generateLanguageNameFields(),
    { name: 'bhajanType', dataType: 'num', editRenderType: 'text' },
  ],
  edges: [
    { name: 'ragam', cardinality: EdgeCardinality.Single, targetNodeTypeId: NodeTypeId.RAGAM },
    { name: 'talam', cardinality: EdgeCardinality.Single, targetNodeTypeId: NodeTypeId.TALAM },
    { name: 'lyrics', cardinality: EdgeCardinality.Single, targetNodeTypeId: NodeTypeId.LYRICS },
    { name: 'deity', cardinality: EdgeCardinality.Single, targetNodeTypeId: NodeTypeId.DEITY },
    { name: 'composer', cardinality: EdgeCardinality.Multi, targetNodeTypeId: NodeTypeId.COMPOSER },
    { name: 'audible', cardinality: EdgeCardinality.Multi, targetNodeTypeId: NodeTypeId.AUDIBLE },
    { name: 'meaning', cardinality: EdgeCardinality.Single, targetNodeTypeId: NodeTypeId.MEANING },
    { name: 'compositionLanguage', cardinality: EdgeCardinality.Single, targetNodeTypeId: NodeTypeId.COMPOSITION_LANGUAGE },
    { name: 'bb', cardinality: EdgeCardinality.Single, targetNodeTypeId: NodeTypeId.BHAJAN_BABIES },
    { name: 'pitch', cardinality: EdgeCardinality.Single, targetNodeTypeId: NodeTypeId.PITCH },
    { name: 'fact', cardinality: EdgeCardinality.Multi, targetNodeTypeId: NodeTypeId.BHAJAN_FACT },
    { name: 'westernNotes', cardinality: EdgeCardinality.Single, targetNodeTypeId: NodeTypeId.WESTERN_NOTES },
    { name: 'languageVariant', cardinality: EdgeCardinality.Multi, targetNodeTypeId: NodeTypeId.BHAJAN },
  ],
};

// NodeTypeId 2: Lyrics
export const LyricsNodeType: NodeTypeDefinition = {
  nodeTypeId: NodeTypeId.LYRICS,
  name: 'lyrics',
  pluralName: 'lyrics',
  description: 'Stores bhajan lyrics in multiple languages',
  fields: [...generateLanguageTextFields()],
  edges: [],
};

// NodeTypeId 3: Ragam
export const RagamNodeType: NodeTypeDefinition = {
  nodeTypeId: NodeTypeId.RAGAM,
  name: 'ragam',
  pluralName: 'ragams',
  description: 'Represents a Carnatic music raga (melodic framework)',
  fields: [
    ...generateLanguageNameFields(),
    { name: 'arohanam', dataType: 'text', editRenderType: 'text' },
    { name: 'avarohanam', dataType: 'text', editRenderType: 'text' },
    { name: 'scale', dataType: 'text', editRenderType: 'text' },
  ],
  edges: [
    { name: 'rashi', cardinality: EdgeCardinality.Single, targetNodeTypeId: NodeTypeId.RASHI },
    { name: 'star', cardinality: EdgeCardinality.Single, targetNodeTypeId: NodeTypeId.STAR },
    { name: 'bodyPart', cardinality: EdgeCardinality.Multi, targetNodeTypeId: NodeTypeId.BODY_PART },
    { name: 'chakra', cardinality: EdgeCardinality.Single, targetNodeTypeId: NodeTypeId.CHAKRA },
    { name: 'rashiElem', cardinality: EdgeCardinality.Single, targetNodeTypeId: NodeTypeId.ELEMENT },
    { name: 'rashiGem', cardinality: EdgeCardinality.Multi, targetNodeTypeId: NodeTypeId.GEM },
    { name: 'rashiColor', cardinality: EdgeCardinality.Single, targetNodeTypeId: NodeTypeId.COLOR },
    { name: 'rashiTree', cardinality: EdgeCardinality.Single, targetNodeTypeId: NodeTypeId.TREE },
    { name: 'chakraElem', cardinality: EdgeCardinality.Single, targetNodeTypeId: NodeTypeId.ELEMENT },
    { name: 'chakraGem', cardinality: EdgeCardinality.Multi, targetNodeTypeId: NodeTypeId.GEM },
    { name: 'chakraColor', cardinality: EdgeCardinality.Single, targetNodeTypeId: NodeTypeId.COLOR },
  ],
};

// NodeTypeId 4: Talam
export const TalamNodeType: NodeTypeDefinition = {
  nodeTypeId: NodeTypeId.TALAM,
  name: 'talam',
  pluralName: 'talams',
  description: 'Represents a Carnatic music tala (rhythmic cycle)',
  fields: [...generateLanguageNameFields()],
  edges: [],
};

// NodeTypeId 5: Composer
export const ComposerNodeType: NodeTypeDefinition = {
  nodeTypeId: NodeTypeId.COMPOSER,
  name: 'composer',
  pluralName: 'composers',
  description: 'Represents the composer/author of bhajans',
  fields: [...generateLanguageNameFields()],
  edges: [],
};

// NodeTypeId 6: Deity
export const DeityNodeType: NodeTypeDefinition = {
  nodeTypeId: NodeTypeId.DEITY,
  name: 'deity',
  pluralName: 'deities',
  description: 'Represents the deity associated with bhajans',
  fields: [...generateLanguageNameFields()],
  edges: [],
};

// NodeTypeId 7: Album
export const AlbumNodeType: NodeTypeDefinition = {
  nodeTypeId: NodeTypeId.ALBUM,
  name: 'album',
  pluralName: 'albums',
  description: 'Represents audio album metadata',
  fields: [
    { name: 'collectionId', dataType: 'text', editRenderType: 'text' },
    { name: 'spotifyAlbumId', dataType: 'text', editRenderType: 'text' },
    { name: 'artistId', dataType: 'text', editRenderType: 'text' },
    { name: 'artistName', dataType: 'text', editRenderType: 'text' },
    { name: 'name', dataType: 'text', editRenderType: 'text' },
    { name: 'audioType', dataType: 'text', editRenderType: 'text' },
    { name: 'artUrl60', dataType: 'text', editRenderType: 'text' },
    { name: 'artUrl100', dataType: 'text', editRenderType: 'text' },
  ],
  edges: [{ name: 'track', cardinality: EdgeCardinality.Multi, targetNodeTypeId: NodeTypeId.TRACK }],
};

// NodeTypeId 8: Track
export const TrackNodeType: NodeTypeDefinition = {
  nodeTypeId: NodeTypeId.TRACK,
  name: 'track',
  pluralName: 'tracks',
  description: 'Represents an individual audio track',
  fields: [
    { name: 'trackId', dataType: 'text', editRenderType: 'text' },
    { name: 'spotifyTrackId', dataType: 'text', editRenderType: 'text' },
    { name: 'name', dataType: 'text', editRenderType: 'text' },
    { name: 'previewUrl', dataType: 'text', editRenderType: 'text' },
    { name: 'discNum', dataType: 'num', editRenderType: 'text' },
    { name: 'trackNum', dataType: 'num', editRenderType: 'text' },
    { name: 'trackDurationMillis', dataType: 'text', editRenderType: 'text' },
  ],
  edges: [],
};

// NodeTypeId 9: Audible
export const AudibleNodeType: NodeTypeDefinition = {
  nodeTypeId: NodeTypeId.AUDIBLE,
  name: 'audible',
  pluralName: 'audibles',
  description: 'Links audio track with lyrics and timing for synchronized playback',
  fields: [
    { name: 'orderIds', dataType: 'text', editRenderType: 'text' },
    { name: 'timingIds', dataType: 'text', editRenderType: 'text' },
  ],
  edges: [
    { name: 'track', cardinality: EdgeCardinality.Single, targetNodeTypeId: NodeTypeId.TRACK },
    { name: 'lyrics', cardinality: EdgeCardinality.Single, targetNodeTypeId: NodeTypeId.LYRICS },
    { name: 'album', cardinality: EdgeCardinality.Single, targetNodeTypeId: NodeTypeId.ALBUM },
  ],
};

// NodeTypeId 10: Meaning
export const MeaningNodeType: NodeTypeDefinition = {
  nodeTypeId: NodeTypeId.MEANING,
  name: 'meaning',
  pluralName: 'meanings',
  description: 'Stores bhajan explanations/meanings in multiple languages',
  fields: [...generateLanguageMeaningFields()],
  edges: [],
};

// NodeTypeId 11/19: Composition Language
export const CompositionLanguageNodeType: NodeTypeDefinition = {
  nodeTypeId: NodeTypeId.COMPOSITION_LANGUAGE,
  name: 'compositionLanguage',
  pluralName: 'compositionLanguages',
  description: 'Represents the original composition language of bhajans',
  fields: [...generateLanguageNameFields()],
  edges: [],
};

// Complete registry of all node type definitions
export const NodeTypeDefinitions: Record<NodeTypeId, NodeTypeDefinition> = {
  [NodeTypeId.BHAJAN]: BhajanNodeType,
  [NodeTypeId.LYRICS]: LyricsNodeType,
  [NodeTypeId.RAGAM]: RagamNodeType,
  [NodeTypeId.TALAM]: TalamNodeType,
  [NodeTypeId.COMPOSER]: ComposerNodeType,
  [NodeTypeId.DEITY]: DeityNodeType,
  [NodeTypeId.ALBUM]: AlbumNodeType,
  [NodeTypeId.TRACK]: TrackNodeType,
  [NodeTypeId.AUDIBLE]: AudibleNodeType,
  [NodeTypeId.MEANING]: MeaningNodeType,
  [NodeTypeId.COMPOSITION_LANGUAGE]: CompositionLanguageNodeType,
  [NodeTypeId.BHAJAN_BABIES]: {
    nodeTypeId: NodeTypeId.BHAJAN_BABIES,
    name: 'bb',
    pluralName: 'bb',
    description: 'Bhajan Babies video information',
    fields: [{ name: 'ytLink', dataType: 'text', editRenderType: 'text' }],
    edges: [],
  },
  [NodeTypeId.RASHI]: {
    nodeTypeId: NodeTypeId.RASHI,
    name: 'rashi',
    pluralName: 'rashis',
    description: 'Zodiac sign / Rashi',
    fields: [...generateLanguageNameFields()],
    edges: [],
  },
  [NodeTypeId.STAR]: {
    nodeTypeId: NodeTypeId.STAR,
    name: 'star',
    pluralName: 'stars',
    description: 'Nakshatra / Star',
    fields: [...generateLanguageNameFields()],
    edges: [],
  },
  [NodeTypeId.BODY_PART]: {
    nodeTypeId: NodeTypeId.BODY_PART,
    name: 'bodyPart',
    pluralName: 'bodyParts',
    description: 'Body part association',
    fields: [...generateLanguageNameFields()],
    edges: [],
  },
  [NodeTypeId.CHAKRA]: {
    nodeTypeId: NodeTypeId.CHAKRA,
    name: 'chakra',
    pluralName: 'chakras',
    description: 'Energy center / Chakra',
    fields: [...generateLanguageNameFields()],
    edges: [],
  },
  [NodeTypeId.ELEMENT]: {
    nodeTypeId: NodeTypeId.ELEMENT,
    name: 'element',
    pluralName: 'elements',
    description: 'Natural element',
    fields: [...generateLanguageNameFields()],
    edges: [],
  },
  [NodeTypeId.GEM]: {
    nodeTypeId: NodeTypeId.GEM,
    name: 'gem',
    pluralName: 'gems',
    description: 'Gemstone / Rashi Gem',
    fields: [...generateLanguageNameFields()],
    edges: [],
  },
  [NodeTypeId.COLOR]: {
    nodeTypeId: NodeTypeId.COLOR,
    name: 'color',
    pluralName: 'colors',
    description: 'Color association',
    fields: [...generateLanguageNameFields()],
    edges: [],
  },
  [NodeTypeId.TREE]: {
    nodeTypeId: NodeTypeId.TREE,
    name: 'tree',
    pluralName: 'trees',
    description: 'Tree association',
    fields: [...generateLanguageNameFields()],
    edges: [],
  },
  [NodeTypeId.COLLECTION]: {
    nodeTypeId: NodeTypeId.COLLECTION,
    name: 'collection',
    pluralName: 'collections',
    description: 'Curated collection of bhajans',
    fields: [
      ...generateLanguageNameFields(),
      { name: 'artUrl', dataType: 'text', editRenderType: 'text' },
    ],
    edges: [
      { name: 'bhajan', cardinality: EdgeCardinality.Multi, targetNodeTypeId: NodeTypeId.BHAJAN },
    ],
  },
  [NodeTypeId.PLACE]: {
    nodeTypeId: NodeTypeId.PLACE,
    name: 'place',
    pluralName: 'places',
    description: 'A place on Earth (used for concerts)',
    fields: [...generateLanguageNameFields()],
    edges: [],
  },
  [NodeTypeId.THITHI]: {
    nodeTypeId: NodeTypeId.THITHI,
    name: 'thithi',
    pluralName: 'thithis',
    description: 'Thithi (lunar day)',
    fields: [...generateLanguageNameFields()],
    edges: [],
  },
  [NodeTypeId.CONCERT_TRACK]: {
    nodeTypeId: NodeTypeId.CONCERT_TRACK,
    name: 'concertTrack',
    pluralName: 'concertTracks',
    description: 'Concert track (part of a concert)',
    fields: [
      ...generateLanguageNameFields(),
      { name: 'instrNums', dataType: 'text', editRenderType: 'text' },
      { name: 'itemNum', dataType: 'text', editRenderType: 'text' },
    ],
    edges: [
      { name: 'bhajan', cardinality: EdgeCardinality.Multi, targetNodeTypeId: NodeTypeId.BHAJAN },
      { name: 'ragam', cardinality: EdgeCardinality.Multi, targetNodeTypeId: NodeTypeId.RAGAM },
    ],
  },
  [NodeTypeId.CONCERT]: {
    nodeTypeId: NodeTypeId.CONCERT,
    name: 'concert',
    pluralName: 'concerts',
    description: 'Concert (Raga Sagara)',
    fields: [
      ...generateLanguageNameFields(),
      { name: 'date', dataType: 'text', editRenderType: 'text' },
      { name: 'medNums', dataType: 'text', editRenderType: 'text' },
      { name: 'remarks', dataType: 'text', editRenderType: 'text' },
    ],
    edges: [
      { name: 'place', cardinality: EdgeCardinality.Single, targetNodeTypeId: NodeTypeId.PLACE },
      { name: 'star', cardinality: EdgeCardinality.Single, targetNodeTypeId: NodeTypeId.STAR },
      { name: 'thithi', cardinality: EdgeCardinality.Multi, targetNodeTypeId: NodeTypeId.THITHI },
      { name: 'concertTrack', cardinality: EdgeCardinality.Multi, targetNodeTypeId: NodeTypeId.CONCERT_TRACK },
    ],
  },
  [NodeTypeId.PITCH]: {
    nodeTypeId: NodeTypeId.PITCH,
    name: 'pitch',
    pluralName: 'pitches',
    description: 'Pitch / Shruti for a bhajan (e.g., Shamasruthi, Madhyamashruti)',
    fields: [...generateLanguageNameFields()],
    edges: [],
  },
  [NodeTypeId.BHAJAN_FACT]: {
    nodeTypeId: NodeTypeId.BHAJAN_FACT,
    name: 'bhajanFact',
    pluralName: 'bhajanFacts',
    description: 'A fact or piece of contextual information about a bhajan',
    fields: [
      ...generateLanguageTextFields(),
      // Bitmask for auto-translated languages (languageCodes order: te,en,kn,ta,hi,ml,or,bn,pa,gu)
      { name: 'autoTranslatedFlags', dataType: 'text', editRenderType: 'text' },
    ],
    edges: [],
  },
  [NodeTypeId.WESTERN_NOTES]: {
    nodeTypeId: NodeTypeId.WESTERN_NOTES,
    name: 'westernNotes',
    pluralName: 'westernNotes',
    description: 'Western music notation (MusicXML) for a bhajan',
    fields: [
      { name: 'musicXml', dataType: 'text', editRenderType: 'longtext' },
    ],
    edges: [],
  },
};

// Utility functions for node type operations

export function getEdgeDefinition(
  nodeTypeId: NodeTypeId,
  edgeName: string
): EdgeDefinition | undefined {
  const nodeType = NodeTypeDefinitions[nodeTypeId];
  if (!nodeType) return undefined;
  return nodeType.edges.find((e) => e.name === edgeName);
}
